import {
  useEffect, useRef, useState
} from "react";
import {
  useNavigate, useSearchParams
} from "react-router-dom";
import {
  getUserFacingApiErrorMessage
} from "../../services/api";
import {
  broadcastOAuthCallbackResult,
  clearOAuthRedirectFlow,
  getOAuthRedirectReturnPath,
  isOAuthRedirectFlow
} from "./oauthCallbackChannel";

interface OAuthCallbackPageProps {
  /** Provider key for the redirect-flow markers and BroadcastChannel name; also the page copy unless `displayName` is set. */
  providerName: string;
  /** Human-readable name for the page copy ("Connecting to {displayName}") when it differs from the key. */
  displayName?: string;
  /** postMessage type emitted to the opener on success (e.g. "SLACK_OAUTH_SUCCESS"). */
  successType: string;
  /** postMessage type emitted to the opener on error (e.g. "SLACK_OAUTH_ERROR"). */
  errorType: string;
  /** Tailwind border-color class for the spinner (e.g. "border-indigo-600"). */
  spinnerColorClass: string;
  /** Provider-specific callback handler (exchanges the code/state with the backend). */
  handleCallback: (args: { code: string; state: string }) => Promise<unknown>;
  /**
   * Whether to flip out of the "processing" spinner into the green success
   * screen before closing/redirecting. Slack closes from the spinner directly;
   * Zoom shows the success screen first.
   */
  showSuccessScreen: boolean;
  /** Popup auto-close delay (ms) after a successful authorization. */
  popupSuccessCloseDelayMs: number;
  /**
   * Non-popup redirect delay (ms) after success. 0 navigates immediately
   * (Slack); a positive value defers (Zoom shows its success screen first).
   */
  navigateDelayOnSuccessMs: number;
  /**
   * Treat a missing opener as a detached popup instead of a top-level flow.
   * Some OAuth providers isolate their page from its opener; the callback can
   * still notify the original tab through BroadcastChannel and close itself.
   */
  supportsDetachedPopup?: boolean;
}

/**
 * Shared OAuth popup-callback page for provider connect flows (Slack, Zoom).
 *
 * Lives at the provider's redirect URI. When opened as a popup it relays the
 * result to the opener via ``postMessage`` and closes; otherwise it redirects
 * to ``/integrations``. The provider-specific bits (service call, message
 * types, copy, success timing) are injected via props so the popup mechanics
 * and origin checks live in exactly one place.
 */
export function OAuthCallbackPage({
  providerName,
  displayName = providerName,
  successType,
  errorType,
  spinnerColorClass,
  handleCallback,
  showSuccessScreen,
  popupSuccessCloseDelayMs,
  navigateDelayOnSuccessMs,
  supportsDetachedPopup = false,
}: OAuthCallbackPageProps) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(true);
  // Exchanging an OAuth code is a one-shot operation. Keep the latest callback
  // prop without making caller reference stability part of effect correctness.
  const handleCallbackRef = useRef(handleCallback);
  useEffect(() => {
    handleCallbackRef.current = handleCallback;
  }, [handleCallback]);
  const [redirectFlow] = useState(() => {
    const state = searchParams.get("state");
    const isRedirect = Boolean(
      supportsDetachedPopup
      && state
      && isOAuthRedirectFlow(providerName, state),
    );
    return {
      isRedirect,
      returnPath: isRedirect && state
        ? getOAuthRedirectReturnPath(providerName, state)
        : null,
    };
  });
  const handlesDetachedPopup = supportsDetachedPopup && !redirectFlow.isRedirect;
  useEffect(() => {
    const state = searchParams.get("state");
    if (state && !handlesDetachedPopup) clearOAuthRedirectFlow(providerName, state);
  }, [handlesDetachedPopup, providerName, searchParams]);

  useEffect(() => {
    const runCallback = async () => {
      const code = searchParams.get("code");
      const state = searchParams.get("state");
      const errorParam = searchParams.get("error");

      const notifyResult = (result: { type: string; error?: string }): void => {
        const scopedResult = state ? { ...result, state } : result;
        if (state) {
          broadcastOAuthCallbackResult(providerName, state, scopedResult);
        }
        if (window.opener) {
          window.opener.postMessage(scopedResult, window.location.origin);
        }
      };

      const closeOrRedirect = (delayMs: number): void => {
        if (window.opener || handlesDetachedPopup) {
          setTimeout(() => window.close(), delayMs);
        } else {
          setTimeout(
            () => navigate(redirectFlow.returnPath ?? "/integrations"),
            delayMs,
          );
        }
      };

      // If OAuth was denied
      if (errorParam) {
        const errorMessage = errorParam === "access_denied"
          ? "Authorization was cancelled"
          : `Authorization failed: ${errorParam}`;
        setError(errorMessage);
        setProcessing(false);

        notifyResult({ type: errorType, error: errorMessage });
        closeOrRedirect(window.opener || handlesDetachedPopup ? 2000 : 3000);
        return;
      }

      // If missing required parameters
      if (!code || !state) {
        setError("Missing required OAuth parameters");
        setProcessing(false);

        notifyResult({ type: errorType, error: "Missing OAuth parameters" });
        closeOrRedirect(window.opener || handlesDetachedPopup ? 2000 : 3000);
        return;
      }

      // Process the OAuth callback
      try {
        await handleCallbackRef.current({ code, state });

        if (showSuccessScreen || (!window.opener && handlesDetachedPopup)) {
          setProcessing(false);
        }

        notifyResult({ type: successType });

        if (window.opener || handlesDetachedPopup) {
          setTimeout(() => window.close(), popupSuccessCloseDelayMs);
        } else if (navigateDelayOnSuccessMs > 0) {
          setTimeout(
            () => navigate(redirectFlow.returnPath ?? "/integrations"),
            navigateDelayOnSuccessMs,
          );
        } else {
          navigate(redirectFlow.returnPath ?? "/integrations");
        }
      } catch (err) {
        const errorMessage = getUserFacingApiErrorMessage(
          err,
          `Couldn't finish connecting ${displayName}. Give it another try.`,
        );
        setError(errorMessage);
        setProcessing(false);

        notifyResult({ type: errorType, error: errorMessage });
        closeOrRedirect(3000);
      }
    };

    runCallback();
  }, [
    errorType,
    handlesDetachedPopup,
    navigate,
    navigateDelayOnSuccessMs,
    popupSuccessCloseDelayMs,
    providerName,
    redirectFlow.returnPath,
    searchParams,
    showSuccessScreen,
    successType,
  ]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8">
        {processing ? (
          <div className="text-center">
            <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${spinnerColorClass} mx-auto mb-4`}></div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Connecting to {displayName}
            </h2>
            <p className="text-gray-600">
              Please wait while we complete the authorization...
            </p>
          </div>
        ) : error ? (
          <div className="text-center">
            <div className="h-12 w-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-6 h-6 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Authorization Failed
            </h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <p className="text-sm text-gray-500">
              {window.opener || handlesDetachedPopup
                ? "This window will close automatically. If it stays open, return to Catch to try again."
                : "Redirecting to integrations page..."}
            </p>
            {!window.opener && handlesDetachedPopup && (
              <a className="inline-block mt-4 text-indigo-600 underline" href="/">
                Back to Catch
              </a>
            )}
          </div>
        ) : (
          <div className="text-center">
            <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-6 h-6 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Successfully Connected!
            </h2>
            <p className="text-gray-600">
              {window.opener || handlesDetachedPopup
                ? "This window will close automatically. If it stays open, you can return to Catch."
                : "Redirecting..."}
            </p>
            {!window.opener && handlesDetachedPopup && (
              <a className="inline-block mt-4 text-indigo-600 underline" href="/">
                Back to Catch
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
